import { useState } from 'react';
import './PreferencesSection.css';

const CATEGORY_META = {
  'ESG': {
    icon: '🌿',
    description: 'Environmental, Social & Governance factors that shape ethical and sustainable investing'
  },
  'Sector': {
    icon: '🏭',
    description: 'Industries and sectors to focus on or avoid in the portfolio'
  },
  'Asset Class': {
    icon: '🎯',
    description: 'Preferred allocation across equity, debt, alternatives and other instruments'
  },
  'Geographic': {
    icon: '🌍',
    description: 'Country or region focus for domestic and international market exposure'
  },
  'Regulatory': {
    icon: '⚖️',
    description: 'Compliance preferences around regulations, tax saving, and lock-in tolerance'
  },
  'Personal': {
    icon: '💡',
    description: 'Lifestyle-driven themes and personal values that guide investment choices'
  }
};

export default function PreferencesSection({ preferenceTemplates, profileData, onSave, saving }) {
  const existing = profileData?.selected_preferences || [];

  // Derive unique categories from templates
  const allCategories = [];
  const seenCats = new Set();
  for (let i = 0; i < preferenceTemplates.length; i++) {
    const cat = preferenceTemplates[i].category;
    if (cat && !seenCats.has(cat)) {
      seenCats.add(cat);
      allCategories.push(cat);
    }
  }

  const [selectedPrefs, setSelectedPrefs] = useState(() => {
    const map = {};
    for (let i = 0; i < existing.length; i++) {
      map[existing[i].preference_template_uid] = { ...(existing[i].field_responses || {}) };
    }
    return map;
  });

  // Init selectedCats from existing selected prefs
  const initialSelectedCats = new Set();
  for (let i = 0; i < existing.length; i++) {
    const tmpl = preferenceTemplates.find(t => t.uid === existing[i].preference_template_uid);
    if (tmpl && tmpl.category) initialSelectedCats.add(tmpl.category);
  }

  const [stage, setStage] = useState('select-cat'); // 'select-cat' | 'select-pref' | 'fill'
  const [selectedCats, setSelectedCats] = useState(initialSelectedCats);
  const [collapsedCards, setCollapsedCards] = useState(new Set());

  function toggleCat(cat) {
    if (selectedCats.has(cat)) {
      const next = new Set(selectedCats);
      next.delete(cat);
      setSelectedCats(next);
      // Deselect any prefs in this category
      const tempsInCat = preferenceTemplates.filter(t => t.category === cat);
      setSelectedPrefs(prev => {
        const updated = { ...prev };
        for (let i = 0; i < tempsInCat.length; i++) {
          delete updated[tempsInCat[i].uid];
        }
        return updated;
      });
    } else {
      const next = new Set(selectedCats);
      next.add(cat);
      setSelectedCats(next);
    }
  }

  function isSelected(templateUid) {
    return Object.prototype.hasOwnProperty.call(selectedPrefs, templateUid);
  }

  function handleTogglePref(template) {
    if (isSelected(template.uid)) {
      setSelectedPrefs(prev => {
        const next = { ...prev };
        delete next[template.uid];
        return next;
      });
      setCollapsedCards(prev => {
        const next = new Set(prev);
        next.delete(template.uid);
        return next;
      });
    } else {
      setSelectedPrefs(prev => ({ ...prev, [template.uid]: {} }));
    }
  }

  function toggleCardCollapse(templateUid) {
    setCollapsedCards(prev => {
      const next = new Set(prev);
      if (next.has(templateUid)) next.delete(templateUid);
      else next.add(templateUid);
      return next;
    });
  }

  function handleFieldChange(templateUid, fieldUid, value) {
    setSelectedPrefs(prev => ({
      ...prev,
      [templateUid]: { ...(prev[templateUid] || {}), [fieldUid]: value }
    }));
  }

  function isPrefComplete(template) {
    const responses = selectedPrefs[template.uid] || {};
    for (let i = 0; i < template.fields.length; i++) {
      const field = template.fields[i];
      if (!field.required) continue;
      const val = responses[field.uid];
      if (!val || val.toString().trim() === '') return false;
    }
    return template.fields.some(f => f.required);
  }

  function handleSave() {
    const payload = [];
    const keys = Object.keys(selectedPrefs);
    for (let i = 0; i < keys.length; i++) {
      payload.push({ preferenceTemplateUid: keys[i], fieldResponses: selectedPrefs[keys[i]] });
    }
    onSave(payload);
  }

  const activeCatTemplates = preferenceTemplates.filter(t => selectedCats.has(t.category));
  const selectedTemplates = preferenceTemplates.filter(t => isSelected(t.uid));
  const totalSelected = selectedTemplates.length;
  const totalComplete = selectedTemplates.filter(t => isPrefComplete(t)).length;

  /* ── Stage 1: Category Cards ────────────────────────────────── */
  if (stage === 'select-cat') {
    return (
      <div className="pref-section">
        <div className="pref-section__stage-header">
          <div className="pref-section__stage-title">What matters to your client?</div>
          <div className="pref-section__stage-sub">Choose the preference areas that are relevant — you can always update later</div>
        </div>

        <div className="pref-section__cat-grid-scroll">
          <div className="pref-section__cat-grid">
            {allCategories.map(cat => {
              const meta = CATEGORY_META[cat] || { icon: '📋', description: '' };
              const isSelectedCat = selectedCats.has(cat);
              const count = preferenceTemplates.filter(t => t.category === cat).length;
              return (
                <button
                  key={cat}
                  className={`pref-section__cat-card ${isSelectedCat ? 'pref-section__cat-card--selected' : ''}`}
                  onClick={() => toggleCat(cat)}
                >
                  {isSelectedCat && (
                    <span className="pref-section__cat-card__check">✓</span>
                  )}
                  <span className="pref-section__cat-card__icon-wrap">{meta.icon}</span>
                  <span className="pref-section__cat-card__name">{cat}</span>
                  <span className="pref-section__cat-card__desc">{meta.description}</span>
                  <span className="pref-section__cat-card__count">{count} preference{count !== 1 ? 's' : ''}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="pref-section__footer">
          <span className="pref-section__count">
            {selectedCats.size === 0
              ? 'No preference areas selected yet'
              : `${selectedCats.size} area${selectedCats.size !== 1 ? 's' : ''} selected`}
          </span>
          <button
            className="btn--primary"
            onClick={() => setStage('select-pref')}
            disabled={selectedCats.size === 0}
          >
            Choose preferences within each →
          </button>
        </div>
      </div>
    );
  }

  /* ── Stage 2: Individual Preference Selection ───────────────── */
  if (stage === 'select-pref') {
    return (
      <div className="pref-section">
        <div className="pref-section__fill-header">
          <div className="pref-section__fill-header-left">
            <button
              className="pref-section__back-btn"
              onClick={() => setStage('select-cat')}
            >
              ← Preference areas
            </button>
            <div>
              <div className="pref-section__stage-title">Select specific preferences</div>
              <div className="pref-section__stage-sub">Pick what's applicable for your client</div>
            </div>
          </div>
        </div>

        <div className="pref-section__palette-scroll">
          {Array.from(selectedCats).map(cat => {
            const meta = CATEGORY_META[cat] || { icon: '📋' };
            const templates = activeCatTemplates.filter(t => t.category === cat);
            return (
              <div key={cat} className="pref-section__cat-group">
                <div className="pref-section__cat-header-palette">
                  <span className="pref-section__cat-icon">{meta.icon}</span>
                  {cat}
                </div>
                <div className="pref-section__cat-row">
                  {templates.map(template => (
                    <button
                      key={template.uid}
                      className={`pref-section__tile ${isSelected(template.uid) ? 'pref-section__tile--selected' : ''}`}
                      onClick={() => handleTogglePref(template)}
                    >
                      <span className="pref-section__tile-name">{template.pref_name}</span>
                      {isSelected(template.uid) && (
                        <>
                          <span className="pref-section__tile-check pref-section__tile-check--normal">✓</span>
                          <span className="pref-section__tile-check pref-section__tile-check--hover">×</span>
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="pref-section__footer">
          <span className="pref-section__count">
            {totalSelected === 0
              ? 'No preferences selected yet'
              : `${totalSelected} preference${totalSelected !== 1 ? 's' : ''} selected`}
          </span>
          <button
            className="btn--primary"
            onClick={() => setStage('fill')}
            disabled={totalSelected === 0}
          >
            Continue to fill details →
          </button>
        </div>
      </div>
    );
  }

  /* ── Stage 3: Preference Details ────────────────────────────── */
  return (
    <div className="pref-section">
      <div className="pref-section__fill-header">
        <div className="pref-section__fill-header-left">
          <button
            className="pref-section__back-btn"
            onClick={() => setStage('select-pref')}
          >
            ← Change preferences
          </button>
          <div>
            <div className="pref-section__stage-title">Fill in preference details</div>
            <div className="pref-section__stage-sub">{totalSelected} preference{totalSelected !== 1 ? 's' : ''} selected · {totalComplete} complete</div>
          </div>
        </div>
        <div className="pref-section__fill-progress-track">
          <div
            className={`pref-section__fill-progress-fill ${totalComplete === totalSelected && totalSelected > 0 ? 'pref-section__fill-progress-fill--done' : ''}`}
            style={{ width: `${totalSelected > 0 ? (totalComplete / totalSelected) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="pref-section__grid-scroll">
        <div className="pref-section__grid">
          {selectedTemplates.map(template => {
            const responses = selectedPrefs[template.uid] || {};
            const complete = isPrefComplete(template);
            const collapsed = collapsedCards.has(template.uid);

            return (
              <div
                key={template.uid}
                className={`pref-section__card ${complete ? 'pref-section__card--complete' : ''} ${collapsed ? 'pref-section__card--collapsed' : ''}`}
              >
                <div className="pref-section__card-header">
                  <div className="pref-section__card-meta">
                    <span className="pref-section__card-name">{template.pref_name}</span>
                    {template.category && <span className="pref-section__card-cat">{template.category}</span>}
                  </div>
                  {complete && !collapsed && (
                    <span className="pref-section__complete-badge">✓</span>
                  )}
                  <button
                    className="pref-section__collapse-btn"
                    onClick={() => toggleCardCollapse(template.uid)}
                    title={collapsed ? 'Expand' : 'Minimise'}
                  >
                    {collapsed ? '▸' : '▾'}
                  </button>
                </div>

                {!collapsed && (
                  <div className="pref-section__card-body">
                    {template.fields.length > 0 ? (
                      <div className="pref-section__card-fields">
                        {template.fields.map(field => (
                          <div key={field.uid} className="pref-section__card-field">
                            <label className="pref-section__field-label">
                              {field.label}
                              {field.required && <span className="pref-section__required">*</span>}
                            </label>
                            {field.field_type === 'dropdown' && (
                              <select
                                className="pref-section__select"
                                value={responses[field.uid] || ''}
                                onChange={e => handleFieldChange(template.uid, field.uid, e.target.value)}
                              >
                                <option value="">Select...</option>
                                {(field.options || []).map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            )}
                            {field.field_type === 'radio' && (
                              <div className="pref-section__radio-group">
                                {(field.options || []).map(opt => (
                                  <label key={opt} className={`pref-section__radio-option ${responses[field.uid] === opt ? 'pref-section__radio-option--selected' : ''}`}>
                                    <input
                                      type="radio"
                                      name={`${template.uid}_${field.uid}`}
                                      checked={responses[field.uid] === opt}
                                      onChange={() => handleFieldChange(template.uid, field.uid, opt)}
                                    />
                                    {opt}
                                  </label>
                                ))}
                              </div>
                            )}
                            {field.field_type === 'checkbox' && (
                              <div className="pref-section__radio-group">
                                {(field.options || []).map(opt => {
                                  const current = Array.isArray(responses[field.uid]) ? responses[field.uid] : [];
                                  return (
                                    <label key={opt} className={`pref-section__radio-option ${current.includes(opt) ? 'pref-section__radio-option--selected' : ''}`}>
                                      <input
                                        type="checkbox"
                                        checked={current.includes(opt)}
                                        onChange={e => {
                                          const newList = [...current];
                                          if (e.target.checked) {
                                            if (!newList.includes(opt)) newList.push(opt);
                                          } else {
                                            const idx = newList.indexOf(opt);
                                            if (idx >= 0) newList.splice(idx, 1);
                                          }
                                          handleFieldChange(template.uid, field.uid, newList);
                                        }}
                                      />
                                      {opt}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                            {(field.field_type === 'text' || field.field_type === 'number') && (
                              <input
                                className="pref-section__input"
                                type={field.field_type === 'number' ? 'number' : 'text'}
                                value={responses[field.uid] || ''}
                                onChange={e => handleFieldChange(template.uid, field.uid, e.target.value)}
                                placeholder={field.hint || ''}
                              />
                            )}
                            {field.field_type === 'textarea' && (
                              <textarea
                                className="pref-section__textarea"
                                value={responses[field.uid] || ''}
                                onChange={e => handleFieldChange(template.uid, field.uid, e.target.value)}
                                placeholder={field.hint || ''}
                                rows={2}
                              />
                            )}
                            {field.hint && field.field_type !== 'radio' && field.field_type !== 'checkbox' && field.field_type !== 'textarea' && (
                              <p className="pref-section__field-hint">{field.hint}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="pref-section__no-fields">No configuration fields for this preference.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="pref-section__footer">
        <span className="pref-section__count">
          {totalComplete === totalSelected && totalSelected > 0 ? 'All preferences detailed!' : ''}
        </span>
        <button className="btn--primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}
